import { Model } from '@nozbe/watermelondb';

import EntryUser from '../../EntryUser';
import InventoryChange from '../../InventoryChange';
import InventoryProduct from '../../InventoryProduct';
import {
    DBServiceOptionsWithImagesAndValidation,
    DBServiceOptionsWithImagesLoggerAndValidation,
} from '../../../../types/dbService';
import {
    EntryModel,
    PreparedDataOnEntryDelete,
} from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { EntryProductModel } from '../../../../types/EntryProduct';
import { EntryUserModel } from '../../../../types/EntryUser';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { toPreparedDataItems } from './common';

type DeleteEntryRelatedData = {
    entryElement: EntryModel;
    entryToDelete: EntryModel;
    entryProceduresToDelete: EntryProcedureModel[];
    entryProductsToDelete: EntryProductModel[];
    inventoryChanges: InventoryChangeModel[];
    entryUsersToDelete: EntryUserModel[];
};

const prepareDeleteEntry = ({ entryElement }: { entryElement: EntryModel }) => {
    return entryElement.prepareMarkAsDeleted();
};

const prepareDeleteEntryProcedures = async ({
    entryElement,
}: {
    entryElement: EntryModel;
}) => {
    const entryProcedures = await entryElement.entryProcedures.fetch();
    const entryProceduresToDelete = entryProcedures.map((entryProcedure) =>
        entryProcedure.prepareMarkAsDeleted(),
    );

    return { entryProceduresToDelete };
};

const prepareDeleteEntryProducts = async ({
    entryElement,
    userId,
    serviceOptions,
}: {
    entryElement: EntryModel;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    const entryProducts = await entryElement.entryProducts.fetch();
    const entryProductsToDelete: EntryProductModel[] = [];
    const inventoryChanges: InventoryChangeModel[] = [];

    const inventoryProductService = new InventoryProduct(serviceOptions);
    const inventoryChangesService = new InventoryChange(serviceOptions);

    await Promise.all(
        entryProducts.map(async (entryProduct) => {
            const inventoryProduct = await inventoryProductService.getByParam(
                'product_id',
                entryProduct.productId,
            );
            if (inventoryProduct.length === 1) {
                const inventoryChange =
                    await inventoryChangesService.prepareAdd(
                        {
                            inventoryProductId: inventoryProduct[0].id,
                            quantityChange: entryProduct.quantity,
                            processAt: new Date(entryElement.loggedTime),
                        },
                        userId,
                    );

                inventoryChanges.push(inventoryChange);
            }
            entryProductsToDelete.push(entryProduct.prepareMarkAsDeleted());
        }),
    );

    return { entryProductsToDelete, inventoryChanges };
};

const prepareDeleteEntryUsers = async ({
    entryId,
    serviceOptions,
}: {
    entryId: string;
    serviceOptions: DBServiceOptionsWithImagesLoggerAndValidation;
}) => {
    const entryUserService = new EntryUser(serviceOptions);
    const entryUsers = await entryUserService.getByEntryId(entryId).fetch();
    const entryUsersToDelete = entryUsers.map((entryUser) =>
        entryUser.prepareMarkAsDeleted(),
    );

    return { entryUsersToDelete };
};

const prepareDeleteEntryRelatedData = async ({
    id,
    userId,
    entryElement,
    serviceOptions,
}: {
    id: string;
    userId: string;
    entryElement: EntryModel;
    serviceOptions: DBServiceOptionsWithImagesLoggerAndValidation;
}) => {
    const entryToDelete = prepareDeleteEntry({ entryElement });
    const proceduresData = await prepareDeleteEntryProcedures({ entryElement });
    const productsData = await prepareDeleteEntryProducts({
        entryElement,
        userId,
        serviceOptions: {
            imageService: serviceOptions.imageService,
            database: serviceOptions.database,
            logDBAction: serviceOptions.logger.logDBAction,
            validation: serviceOptions.validation,
        },
    });
    const usersData = await prepareDeleteEntryUsers({
        entryId: id,
        serviceOptions,
    });

    return {
        entryElement,
        entryToDelete,
        ...proceduresData,
        ...productsData,
        ...usersData,
    };
};

const prepareDeleteEntryById = async ({
    entryElement,
    userId,
    serviceOptions,
}: {
    entryElement: EntryModel;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}): Promise<Model[]> => {
    const entryToDelete = prepareDeleteEntry({ entryElement });
    const proceduresData = await prepareDeleteEntryProcedures({ entryElement });
    const productsData = await prepareDeleteEntryProducts({
        entryElement,
        userId,
        serviceOptions,
    });

    return [
        ...productsData.inventoryChanges,
        ...proceduresData.entryProceduresToDelete,
        ...productsData.entryProductsToDelete,
        entryToDelete,
    ];
};

const appendPreparedInventoryChanges = (
    preparedData: PreparedDataOnEntryDelete[],
    deleteData: Pick<DeleteEntryRelatedData, 'inventoryChanges'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            deleteData.inventoryChanges,
            'inventory_changes',
            'add',
        ),
    ];
};

const appendPreparedDeleteEntryProcedures = (
    preparedData: PreparedDataOnEntryDelete[],
    deleteData: Pick<DeleteEntryRelatedData, 'entryProceduresToDelete'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            deleteData.entryProceduresToDelete,
            'entry_procedures',
            'delete',
        ),
    ];
};

const appendPreparedDeleteEntryProducts = (
    preparedData: PreparedDataOnEntryDelete[],
    deleteData: Pick<DeleteEntryRelatedData, 'entryProductsToDelete'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            deleteData.entryProductsToDelete,
            'entry_products',
            'delete',
        ),
    ];
};

const appendPreparedDeleteEntryUsers = (
    preparedData: PreparedDataOnEntryDelete[],
    deleteData: Pick<DeleteEntryRelatedData, 'entryUsersToDelete'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            deleteData.entryUsersToDelete,
            'entry_users',
            'delete',
        ),
    ];
};

const appendPreparedDeleteEntry = (
    preparedData: PreparedDataOnEntryDelete[],
    deleteData: Pick<DeleteEntryRelatedData, 'entryToDelete'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems([deleteData.entryToDelete], 'entries', 'delete'),
    ];
};

const flattenDeleteRelatedDataToModels = (
    deleteData: DeleteEntryRelatedData,
): Model[] => {
    return [
        ...deleteData.inventoryChanges,
        ...deleteData.entryProceduresToDelete,
        ...deleteData.entryProductsToDelete,
        ...deleteData.entryUsersToDelete,
        deleteData.entryToDelete,
    ];
};

const logDeleteDBActions = ({
    deleteData,
    logDBAction,
    tableName,
}: {
    deleteData: DeleteEntryRelatedData;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message:
            'Delete entry - delete entry procedures, entry products and entry',
        modelName: tableName,
        payload: flattenDeleteRelatedDataToModels(deleteData),
    });
};

export {
    appendPreparedDeleteEntry,
    appendPreparedDeleteEntryProcedures,
    appendPreparedDeleteEntryProducts,
    appendPreparedDeleteEntryUsers,
    appendPreparedInventoryChanges,
    flattenDeleteRelatedDataToModels,
    logDeleteDBActions,
    prepareDeleteEntryById,
    prepareDeleteEntryRelatedData,
};

export type { DeleteEntryRelatedData };
